import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { clearOnboardingComplete, isOnboardingComplete, setOnboardingComplete } from '../../utils/onboardingStorage';
import './Auth.css';

function OAuth2Success() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser, setToken, setIsAuthenticated } = useAuth();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processOAuthCallback = async () => {
    const token = searchParams.get('token');
    const userId = searchParams.get('userId');
    const isNewUser = searchParams.get('isNewUser') === 'true';
    const email = searchParams.get('email');

      console.log('OAuth2Success - Processing callback:', { token: token ? 'present' : 'missing', userId, isNewUser, email });

      if (!token || !userId) {
        console.error('OAuth callback missing required parameters:', { token: !!token, userId: !!userId });
        setProcessing(false);
        navigate('/login?error=oauth_failed&message=Authentication failed. Please try signing in again.', { replace: true });
        return;
      }

      try {
        // Get firstName and lastName from URL parameters (passed from backend)
        const firstName = searchParams.get('firstName') || '';
        const lastName = searchParams.get('lastName') || '';

        console.log('OAuth2Success - Storing user data:', { userId, email, firstName, lastName, isNewUser });

        // Store token and user info in localStorage FIRST
      localStorage.setItem('authToken', token);
        
        // Store complete user data
      const userData = {
        userId: userId,
        id: userId,
          email: email,
          firstName: firstName,
          lastName: lastName,
          isNewUser: isNewUser,
          emailVerified: true // OAuth emails are pre-verified
      };
      localStorage.setItem('user', JSON.stringify(userData));

        console.log('OAuth2Success - Updating AuthContext state');

        // Update AuthContext state immediately (before redirect)
        // This ensures ProtectedRoute sees the user as authenticated
        if (setToken) {
          setToken(token);
        }
        if (updateUser) {
          updateUser(userData);
        }
        if (setIsAuthenticated) {
          setIsAuthenticated(true);
        }
        
        // Wait a bit longer to ensure state is fully updated
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setProcessing(false);
        
        console.log('OAuth2Success - Redirecting:', { isNewUser, destination: isNewUser ? '/onboarding' : '/dashboard' });

        // For new OAuth users, redirect to onboarding to complete profile setup
        if (isNewUser) {
          clearOnboardingComplete(String(userId));
          window.location.href = '/onboarding';
          return;
        }

        // For existing users, verify onboarding status by checking profile
        let hasProfile = false;
        try {
          const profileResponse = await api.getPregnancyProfile(userId);
          if (profileResponse && profileResponse.id) {
            hasProfile = true;
            setOnboardingComplete(String(userId));
          } else {
            clearOnboardingComplete(String(userId));
          }
        } catch (profileError) {
          const errorStatus = profileError.status || profileError.response?.status;
          if (errorStatus === 404) {
            hasProfile = false;
            clearOnboardingComplete(String(userId));
          } else {
            // If profile check fails, fall back to local flag
            hasProfile = isOnboardingComplete(String(userId));
          }
        }

        // Redirect based on onboarding status
        if (!hasProfile) {
          window.location.href = '/onboarding';
        } else {
          window.location.href = '/dashboard';
        }
      } catch (error) {
        console.error('Error processing OAuth callback:', error);
        setProcessing(false);
        navigate('/login?error=oauth_failed&message=Unable to complete sign-in. Please try again.', { replace: true });
    }
    };

    processOAuthCallback();
  }, [searchParams, navigate, updateUser, setToken, setIsAuthenticated]);

  if (processing) {
  return (
      <div className="auth-container-feminine">
        <div className="auth-card-feminine">
          <LoadingSpinner message={t('auth.oauth.settingUp')} />
      </div>
    </div>
  );
  }

  return null;
}

export default OAuth2Success;
